import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, Search, Users, Radio } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { books, shofars, bundles, subscriptionTiers, testimonials } from '../data/mock';

const Home = () => {
  const featuredBooks = books.slice(0, 3);
  const featuredBundles = bundles.slice(0, 3);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://images.unsplash.com/photo-1499209974431-9dddcece7f88?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxzcGlyaXR1YWwlMjBhd2FrZW5pbmd8ZW58MHx8fHwxNzczNTM1Nzg5fDA&ixlib=rb-4.1.0&q=85"
            alt="Spiritual awakening"
            className="w-full h-full object-cover"
          />
        </div>

        <div className="container mx-auto px-4 py-24 md:py-32 relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <Badge className="bg-amber-600 hover:bg-amber-700 text-white border-0 text-sm px-4 py-1">
              📯 Sound the Trumpet
            </Badge>
            
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold leading-tight">
              What Does the Grammar <span className="text-amber-400">Actually Say</span>?
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto">
              Applying plain grammatical analysis to biblical prophecy. No speculation. No tradition. Just the text.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
              <Link to="/ebook/prologue">
                <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white text-lg px-8 py-6">
                  Read Free Prologue
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/subscriptions">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 text-lg px-8 py-6">
                  View Subscriptions
                </Button>
              </Link>
            </div>

            <p className="text-sm text-gray-400 pt-4">
              No login required · Learn the Grammar Integrity Test method
            </p>
          </div>
        </div>

        {/* Decorative Bottom Wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* The GIT Method Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
              The Grammar Integrity Test
            </h2>
            <p className="text-xl text-gray-600">
              A six-protocol system that applies plain grammatical analysis to any biblical passage, theological claim, or doctrinal position.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              { number: '1', title: 'Subject Test', description: 'Who is the text talking about?' },
              { number: '2', title: 'Verb Test', description: 'What does the text say actually happens?' },
              { number: '3', title: 'Timeline Test', description: 'When does the text say this takes place?' },
              { number: '4', title: 'Location Test', description: 'Where does the text place the action?' },
              { number: '5', title: 'Purpose Test', description: 'Why does the text say this happens?' },
              { number: '6', title: 'Method Test', description: 'How does the text say it occurs?' }
            ].map((protocol) => (
              <Card key={protocol.number} className="border-2 hover:border-amber-500 transition-all hover:shadow-lg">
                <CardHeader>
                  <div className="w-12 h-12 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-2xl font-bold mb-4">
                    {protocol.number}
                  </div>
                  <CardTitle className="text-xl">{protocol.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">{protocol.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button size="lg" variant="outline" className="text-lg px-8">
              Try the GIT Investigator
              <Search className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Featured Books Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">Featured Books</h2>
            <p className="text-xl text-gray-600">Start your journey into post-tribulational truth</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {featuredBooks.map((book) => (
              <Card key={book.id} className="overflow-hidden hover:shadow-xl transition-shadow">
                <div className="aspect-[3/4] overflow-hidden bg-gray-200">
                  <img
                    src={book.image}
                    alt={book.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <CardHeader>
                  <CardTitle className="text-lg line-clamp-2">{book.title}</CardTitle>
                  <CardDescription className="line-clamp-3">{book.description}</CardDescription>
                </CardHeader>
                <CardFooter className="flex justify-between items-center">
                  <span className="text-2xl font-bold text-gray-900">${book.price}</span>
                  <Button>Add to Cart</Button>
                </CardFooter>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/store">
              <Button size="lg" variant="outline" className="text-lg px-8">
                View All Books
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Bundles Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="bg-green-600 text-white mb-4">Best Value</Badge>
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">Book Bundles</h2>
            <p className="text-xl text-gray-600">Save up to 40% + get free digital access</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {featuredBundles.map((bundle) => (
              <Card key={bundle.id} className="border-2 hover:border-amber-500 transition-all relative">
                {bundle.badge && (
                  <Badge className="absolute top-4 right-4 bg-amber-600 text-white">
                    {bundle.badge}
                  </Badge>
                )}
                <CardHeader>
                  <CardTitle className="text-xl">{bundle.title}</CardTitle>
                  <CardDescription>{bundle.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-gray-900">${bundle.price}</span>
                    <span className="text-lg text-gray-400 line-through">${bundle.originalPrice}</span>
                  </div>
                  <p className="text-green-600 font-medium">Save ${bundle.savings}</p>
                  <ul className="space-y-2 text-sm text-gray-600">
                    {bundle.includes.map((item, idx) => (
                      <li key={idx} className="flex items-start">
                        <span className="text-green-600 mr-2">✓</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter>
                  <Button className="w-full">Get This Bundle</Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Subscription Tiers */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">Digital Access Tiers</h2>
            <p className="text-xl text-gray-600">Choose the level that's right for your study journey</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {subscriptionTiers.map((tier) => (
              <Card key={tier.id} className={`relative ${tier.popular ? 'border-amber-500 border-2 shadow-xl' : ''}`}>
                {tier.popular && (
                  <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-amber-600 text-white">
                    Most Popular
                  </Badge>
                )}
                <CardHeader>
                  <CardTitle className="text-2xl">{tier.name}</CardTitle>
                  <div className="mt-4">
                    {tier.price === 0 ? (
                      <span className="text-4xl font-bold">Free</span>
                    ) : (
                      <>
                        <span className="text-4xl font-bold">${tier.price}</span>
                        <span className="text-gray-600">/{tier.period}</span>
                      </>
                    )}
                  </div>
                  <CardDescription className="mt-2">{tier.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    {tier.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <span className="text-green-600 mr-2">✓</span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter>
                  <Button className={`w-full ${tier.popular ? 'bg-amber-600 hover:bg-amber-700' : ''}`}>
                    {tier.buttonText}
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/subscriptions">
              <Button size="lg" variant="outline" className="text-lg px-8">
                Compare All Features
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Kingdom Escort Guard Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <Badge className="bg-amber-600 text-white mb-4">Join the Guard</Badge>
                <h2 className="text-3xl md:text-5xl font-bold mb-6">The Kingdom Escort Guard</h2>
                <p className="text-xl text-gray-300 mb-6">
                  A private registry of trained shofar blowers available to churches, ministries, and events across the nation.
                </p>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <span className="text-amber-400 mr-3">✓</span>
                    <span>Professional shofar training with four certification levels</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-amber-400 mr-3">✓</span>
                    <span>Connect with churches needing shofar voices</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-amber-400 mr-3">✓</span>
                    <span>Access to Scholar-tier digital content included</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-amber-400 mr-3">✓</span>
                    <span>Annual Kingdom Escort Guard Assembly</span>
                  </li>
                </ul>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/kingdom-guard">
                    <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white">
                      Learn More
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                    Request a Blower
                  </Button>
                </div>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1658343546442-85e82a8d5186?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxyYW0lMjBob3JufGVufDB8fHx8MTc3MzUzNTgxNHww&ixlib=rb-4.1.0&q=85"
                  alt="Shofar"
                  className="rounded-lg shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Media Integration Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">Podcast & Video Teaching</h2>
            <p className="text-xl text-gray-600">Weekly episodes exploring post-trib eschatology verse by verse</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <Card className="overflow-hidden">
              <div className="aspect-video bg-gray-200 flex items-center justify-center">
                <Radio className="h-16 w-16 text-gray-400" />
              </div>
              <CardHeader>
                <CardTitle>Latest Episode</CardTitle>
                <CardDescription>Matthew 24:29-31 - The Grammar of His Coming</CardDescription>
              </CardHeader>
              <CardFooter>
                <Button className="w-full">Watch Now</Button>
              </CardFooter>
            </Card>

            <Card className="overflow-hidden">
              <div className="aspect-video bg-gray-200 flex items-center justify-center">
                <BookOpen className="h-16 w-16 text-gray-400" />
              </div>
              <CardHeader>
                <CardTitle>Episode Library</CardTitle>
                <CardDescription>Access 100+ teaching episodes organized by topic</CardDescription>
              </CardHeader>
              <CardFooter>
                <Button className="w-full" variant="outline">Browse Episodes</Button>
              </CardFooter>
            </Card>
          </div>

          <div className="text-center mt-12">
            <Link to="/media">
              <Button size="lg" className="text-lg px-8">
                View All Media
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">What Readers Are Saying</h2>
            <p className="text-xl text-gray-600">Lives and churches transformed by grammatical truth</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-2">
                <CardHeader>
                  <div className="flex gap-1 mb-2">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <span key={i} className="text-amber-500">★</span>
                    ))}
                  </div>
                  <CardTitle className="text-lg">{testimonial.name}</CardTitle>
                  <CardDescription>{testimonial.role}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 italic">"{testimonial.content}"</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-r from-amber-600 to-amber-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-8">
            <h2 className="text-4xl md:text-5xl font-bold">
              Ready to Discover What the Grammar Actually Says?
            </h2>
            <p className="text-xl text-amber-100">
              Start with Little Book 2 completely free. No credit card required.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/subscriptions">
                <Button size="lg" className="bg-white text-amber-700 hover:bg-gray-100 text-lg px-8 py-6">
                  Start Reading Free
                  <BookOpen className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/store">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 text-lg px-8 py-6">
                  Browse the Store
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
